// Cross-subdomain dark/light sync. Zudoku's theme switcher is next-themes
// (localStorage key "theme", values light|dark|system) mounted inside Zudoku
// itself, so we can't wrap it — instead this side-effect module (imported by
// zudoku.config.tsx, no-op during build-time prerendering):
//  1. adopts the shared `theme` cookie into localStorage before next-themes
//     mounts (and applies the class right away to minimize the flash), and
//  2. observes the <html> class attribute — next-themes rewrites it on every
//     change — to write the user's new choice back to the shared cookie.

import {
  getSharedTheme,
  setSharedTheme,
  type SharedTheme,
} from "@regexsolver/web-shared/theme";

const THEME_STORAGE_KEY = "theme"; // next-themes' default storageKey
const COOKIE_DOMAIN = process.env.ZUDOKU_PUBLIC_COOKIE_DOMAIN;

if (typeof window !== "undefined") {
  // A choice made on any subdomain wins over this site's own saved preference.
  const shared = getSharedTheme();
  if (shared && localStorage.getItem(THEME_STORAGE_KEY) !== shared) {
    localStorage.setItem(THEME_STORAGE_KEY, shared);
    const resolved =
      shared === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : shared;
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(resolved);
  }

  // Only explicit changes propagate: lastSynced starts at the current effective
  // value, so next-themes' initial attribute writes on mount don't push this
  // site's old local preference into the shared cookie.
  let lastSynced: string | null =
    shared ?? localStorage.getItem(THEME_STORAGE_KEY);
  new MutationObserver(() => {
    const theme = localStorage.getItem(THEME_STORAGE_KEY) as SharedTheme | null;
    if (theme && theme !== lastSynced) {
      lastSynced = theme;
      setSharedTheme(theme, COOKIE_DOMAIN);
    }
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
}
